import dayjs from 'dayjs';
import { ESSENTIALS_SLUG, PLUS_SLUG, PRO_SLUG } from 'mdc-constants';
import { TWO_HOURS_IN_MILLISECONDS } from 'mdc-constants';
import { useGraphQLClient } from 'providers';
import { useMemo } from 'react';
import { useUserSubscriptionQuery } from '../../graphql';
import useAccessToken from '../useAccessToken';
export const useSubscription = () => {
  const { data: accessToken } = useAccessToken();

  const { graphQLClient } = useGraphQLClient(accessToken);

  const {
    data: currentSubscription,
    isLoading,
    isError,
    refetch,
  } = useUserSubscriptionQuery(graphQLClient, undefined, {
    enabled: !!accessToken,
    staleTime: TWO_HOURS_IN_MILLISECONDS,
    gcTime: TWO_HOURS_IN_MILLISECONDS,
  });

  const canUpgrade = useMemo<boolean>(() => {
    if (currentSubscription?.subscription?.is_free) {
      return true;
    }

    if (
      currentSubscription?.subscription?.plan?.slug === PLUS_SLUG ||
      currentSubscription?.subscription?.plan?.slug === ESSENTIALS_SLUG
    ) {
      return true;
    }

    return false;
  }, [currentSubscription?.subscription]);

  const payedPlanHasAvailable = useMemo<boolean>(
    () =>
      !currentSubscription?.subscription?.is_free && (currentSubscription?.subscription?.available_licenses || 0) > 0,
    [currentSubscription?.subscription],
  );

  const isOnActiveGrace = useMemo(
    () => currentSubscription?.subscription?.status === 'active' && currentSubscription?.subscription?.is_on_grace,
    [currentSubscription?.subscription],
  );

  const subscriptionEnd = useMemo(() => {
    if (currentSubscription?.subscription?.end_date) {
      try {
        return dayjs(currentSubscription?.subscription?.end_date).format('DD MMM YYYY');
      } catch (_error) {}
    }

    return '---';
  }, [currentSubscription?.subscription?.end_date]);

  const nextBillingDate = useMemo(() => {
    if (currentSubscription?.subscription?.next_billing_date) {
      try {
        return dayjs(currentSubscription?.subscription?.next_billing_date).format('DD MMM YYYY');
      } catch (_error) {}
    }

    return '---';
  }, [currentSubscription?.subscription?.next_billing_date]);

  const totalCosts = useMemo(() => {
    if (currentSubscription?.subscription?.is_free) {
      return 0;
    }

    if (currentSubscription?.subscription?.plan?.total && currentSubscription?.subscription?.plan?.cost) {
      if (currentSubscription?.subscription?.plan?.total > 0) {
        return currentSubscription?.subscription?.plan?.total * currentSubscription?.subscription?.plan?.cost;
      }
    }

    return 0;
  }, [currentSubscription?.subscription]);

  const currencySymbol = useMemo(() => {
    const currencyMap = new Map([
      ['euro', '€'],
      ['eur', '€'],
      ['usd', '$'],
    ]);

    if (currentSubscription?.subscription?.currency) {
      if (currencyMap.has(currentSubscription?.subscription?.currency)) {
        return currencyMap.get(currentSubscription?.subscription?.currency);
      }
    }

    return '€';
  }, [currentSubscription?.subscription?.currency]);

  const recurrence = useMemo(() => {
    const recurrenceMap = new Map([
      ['year', 'Annually'],
      ['month', 'Monthly'],
    ]);

    if (currentSubscription?.subscription?.interval) {
      if (recurrenceMap.has(currentSubscription?.subscription?.interval)) {
        return recurrenceMap.get(currentSubscription?.subscription?.interval);
      }
    }

    return currentSubscription?.subscription?.interval;
  }, [currentSubscription?.subscription?.interval]);

  const subscriptionSince = useMemo(() => {
    if (currentSubscription?.subscription?.first_subscription_date) {
      try {
        return dayjs(currentSubscription?.subscription?.first_subscription_date).format('DD MMM YYYY');
      } catch (_error) {}
    }

    return '---';
  }, [currentSubscription?.subscription?.first_subscription_date]);

  const isProUser = useMemo(
    () => currentSubscription?.subscription?.plan?.slug === PRO_SLUG,
    [currentSubscription?.subscription],
  );

  const alertsLimitReached = useMemo<boolean>(() => {
    const currentAvailableAlerts = currentSubscription?.subscription?.available_active_alert_slots || 0;
    return !(currentAvailableAlerts > 0);
  }, [currentSubscription?.subscription?.available_active_alert_slots]);

  return {
    alertsLimitReached,
    isProUser,
    isLoading,
    isLoadingSubscription: isLoading,
    isError,
    subscription: currentSubscription?.subscription,
    isSubscribed: !currentSubscription?.subscription?.is_free,
    canUpgrade,
    payedPlanHasAvailable,
    isOnActiveGrace,
    subscriptionEnd,
    refetch,
    totalCosts,
    currencySymbol,
    recurrence,
    subscriptionSince,
    nextBillingDate,
  };
};
